import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../utils/auth.js';

export default function QueueStatus() {
    const { currentUser } = useContext(AuthContext);
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const [isLoading, setIsLoading] = useState(false);
    const [queueData, setQueueData] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchQueueStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const idToken = await currentUser.getIdToken();
            const response = await axios.get(`${serverUrl}/admin/getWorkerJobs`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${idToken}`
                },
                timeout: 10000
            });

            setQueueData(response.data);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to fetch queue status');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, serverUrl]);

    useEffect(() => {
        fetchQueueStatus();
    }, [fetchQueueStatus]);

    const getStatusColor = (active, waiting) => {
        if (active > 0) return 'bg-emerald-500';
        if (waiting > 0) return 'bg-amber-500';
        return 'bg-slate-300';
    };

    const getQueueTypeColor = (name) => {
        if (name.includes('gpt')) return 'from-violet-500 to-purple-600';
        if (name.includes('azure')) return 'from-sky-500 to-blue-600';
        if (name.includes('edenAI')) return 'from-emerald-500 to-teal-600';
        if (name.includes('classification')) return 'from-amber-500 to-orange-600';
        return 'from-slate-500 to-slate-600';
    };

    const groupQueuesByType = (queues) => {
        const groups = {
            'GPT Queues': [],
            'Azure Queues': [],
            'EdenAI Queues': [],
            'Classification Queues': []
        };

        queues?.forEach(queue => {
            if (queue.name.includes('classification')) {
                groups['Classification Queues'].push(queue);
            } else if (queue.name.includes('gpt')) {
                groups['GPT Queues'].push(queue);
            } else if (queue.name.includes('azure')) {
                groups['Azure Queues'].push(queue);
            } else if (queue.name.includes('edenAI')) {
                groups['EdenAI Queues'].push(queue);
            }
        });

        return groups;
    };

    const formatQueueName = (name) => {
        return name
            .replace('gpt-queue', 'GPT')
            .replace('azure-queue', 'Azure')
            .replace('edenAI-queue', 'EdenAI')
            .replace('document-classification-azure-queue', 'Classification Azure')
            .replace('document-classification-gpt-queue', 'Classification GPT')
            .replace(/-/g, ' ');
    };

    const queueGroups = queueData?.queues ? groupQueuesByType(queueData.queues) : {};

    return (
        <div className="space-y-6 pt-4">
            {/* Header with controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 shadow-lg">
                <div>
                    <h2 className="text-2xl font-bold text-white">Queue Status Monitor</h2>
                    <p className="text-slate-400 text-sm mt-1">
                        Real-time worker queue monitoring
                    </p>
                    {lastUpdated && (
                        <p className="text-slate-500 text-xs mt-2">
                            Last updated: {lastUpdated.toLocaleTimeString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchQueueStatus}
                        disabled={isLoading}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${isLoading
                                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                                : 'bg-indigo-600 text-white hover:bg-indigo-500 active:scale-95'
                            }`}
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        )}
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-red-700 font-medium">{error}</span>
                    </div>
                </div>
            )}

            {/* Totals Overview */}
            {queueData?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-slate-800">{queueData.totals.total}</div>
                        <div className="text-sm text-slate-500 mt-1">Total Pending</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-emerald-600">{queueData.totals.active}</div>
                        <div className="text-sm text-slate-500 mt-1">Active</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-amber-600">{queueData.totals.waiting}</div>
                        <div className="text-sm text-slate-500 mt-1">Waiting</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-sky-600">{queueData.totals.delayed}</div>
                        <div className="text-sm text-slate-500 mt-1">Delayed</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-green-600">{queueData.totals.completed}</div>
                        <div className="text-sm text-slate-500 mt-1">Completed</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-red-600">{queueData.totals.failed}</div>
                        <div className="text-sm text-slate-500 mt-1">Failed</div>
                    </div>
                </div>
            )}

            {/* GPT and Azure Queues - Side by Side */}
            {(queueGroups['GPT Queues']?.length > 0 || queueGroups['Azure Queues']?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* GPT Queues */}
                    {queueGroups['GPT Queues']?.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                                <h3 className="text-lg font-semibold text-slate-800">GPT Queues</h3>
                                <p className="text-sm text-slate-500">
                                    {queueGroups['GPT Queues'].reduce((acc, q) => acc + q.active, 0)} active, {queueGroups['GPT Queues'].reduce((acc, q) => acc + q.waiting, 0)} waiting
                                </p>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {queueGroups['GPT Queues'].map((queue) => (
                                    <div key={queue.name} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${getStatusColor(queue.active, queue.waiting)}`} />
                                                <span className="font-medium text-slate-700 text-sm">{formatQueueName(queue.name)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs">
                                                <div className="text-center">
                                                    <div className="font-semibold text-emerald-600">{queue.active}</div>
                                                    <div className="text-slate-400">Active</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-amber-600">{queue.waiting}</div>
                                                    <div className="text-slate-400">Wait</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-sky-600">{queue.delayed}</div>
                                                    <div className="text-slate-400">Delay</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-green-600">{queue.completed}</div>
                                                    <div className="text-slate-400">Done</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-red-600">{queue.failed}</div>
                                                    <div className="text-slate-400">Fail</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Progress bar */}
                                        {queue.total > 0 && (
                                            <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full bg-gradient-to-r ${getQueueTypeColor(queue.name)} transition-all duration-500`}
                                                    style={{ width: `${Math.min((queue.active / Math.max(queue.total, 1)) * 100, 100)}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Azure Queues */}
                    {queueGroups['Azure Queues']?.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                                <h3 className="text-lg font-semibold text-slate-800">Azure Queues</h3>
                                <p className="text-sm text-slate-500">
                                    {queueGroups['Azure Queues'].reduce((acc, q) => acc + q.active, 0)} active, {queueGroups['Azure Queues'].reduce((acc, q) => acc + q.waiting, 0)} waiting
                                </p>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {queueGroups['Azure Queues'].map((queue) => (
                                    <div key={queue.name} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${getStatusColor(queue.active, queue.waiting)}`} />
                                                <span className="font-medium text-slate-700 text-sm">{formatQueueName(queue.name)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs">
                                                <div className="text-center">
                                                    <div className="font-semibold text-emerald-600">{queue.active}</div>
                                                    <div className="text-slate-400">Active</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-amber-600">{queue.waiting}</div>
                                                    <div className="text-slate-400">Wait</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-sky-600">{queue.delayed}</div>
                                                    <div className="text-slate-400">Delay</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-green-600">{queue.completed}</div>
                                                    <div className="text-slate-400">Done</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-semibold text-red-600">{queue.failed}</div>
                                                    <div className="text-slate-400">Fail</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Progress bar */}
                                        {queue.total > 0 && (
                                            <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full bg-gradient-to-r ${getQueueTypeColor(queue.name)} transition-all duration-500`}
                                                    style={{ width: `${Math.min((queue.active / Math.max(queue.total, 1)) * 100, 100)}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Other Queue Groups (EdenAI and Classification) - Side by Side */}
            {(queueGroups['EdenAI Queues']?.length > 0 || queueGroups['Classification Queues']?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {['EdenAI Queues', 'Classification Queues'].map((groupName) => {
                        const queues = queueGroups[groupName];
                        if (!queues || queues.length === 0) return null;

                        return (
                            <div key={groupName} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                                    <h3 className="text-lg font-semibold text-slate-800">{groupName}</h3>
                                    <p className="text-sm text-slate-500">
                                        {queues.reduce((acc, q) => acc + q.active, 0)} active, {queues.reduce((acc, q) => acc + q.waiting, 0)} waiting
                                    </p>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {queues.map((queue) => (
                                        <div key={queue.name} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${getStatusColor(queue.active, queue.waiting)}`} />
                                                    <span className="font-medium text-slate-700 text-sm">{formatQueueName(queue.name)}</span>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs">
                                                    <div className="text-center">
                                                        <div className="font-semibold text-emerald-600">{queue.active}</div>
                                                        <div className="text-slate-400">Active</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-amber-600">{queue.waiting}</div>
                                                        <div className="text-slate-400">Wait</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-sky-600">{queue.delayed}</div>
                                                        <div className="text-slate-400">Delay</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-green-600">{queue.completed}</div>
                                                        <div className="text-slate-400">Done</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-red-600">{queue.failed}</div>
                                                        <div className="text-slate-400">Fail</div>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Progress bar */}
                                            {queue.total > 0 && (
                                                <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full bg-gradient-to-r ${getQueueTypeColor(queue.name)} transition-all duration-500`}
                                                        style={{ width: `${Math.min((queue.active / Math.max(queue.total, 1)) * 100, 100)}%` }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Loading skeleton */}
            {isLoading && !queueData && (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 animate-pulse">
                            <div className="h-6 bg-slate-200 rounded w-1/4 mb-4" />
                            <div className="space-y-3">
                                <div className="h-4 bg-slate-100 rounded w-full" />
                                <div className="h-4 bg-slate-100 rounded w-3/4" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && (!queueData || queueData.queues?.length === 0) && (
                <div className="bg-white rounded-xl p-12 shadow-sm border border-slate-100 text-center">
                    <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-slate-900">No queue data available</h3>
                    <p className="mt-2 text-sm text-slate-500">Try refreshing to fetch the latest queue status.</p>
                </div>
            )}
        </div>
    );
}

